import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, ShareNetwork, X } from "@phosphor-icons/react";
import QRCode from "qrcode";

const fmtTime = (s) => {
  const sec = Math.max(0, Number(s) || 0);
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${sec}s`;
};

/**
 * Genera una imagen 1080x1920 (Instagram story) con los KPIs del usuario.
 */
export default function ShareStatsModal({ open, onClose, kpis, user }) {
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState("");

  useEffect(() => {
    if (!open) return;
    // Wait next frame so the canvas (mounted via hidden class) has dims
    const t = setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      canvas.width = 1080;
      canvas.height = 1920;
      const ctx = canvas.getContext("2d");

    // BG gradient
    const g = ctx.createLinearGradient(0, 0, 0, 1920);
    g.addColorStop(0, "#0A0A0A");
    g.addColorStop(0.5, "#070707");
    g.addColorStop(1, "#0A0A0A");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 1080, 1920);

    // Neon glow circles
    const glow = (x, y, r, color) => {
      const grad = ctx.createRadialGradient(x, y, 0, x, y, r);
      grad.addColorStop(0, color);
      grad.addColorStop(1, "transparent");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, 1080, 1920);
    };
    glow(150, 200, 400, "rgba(204,255,0,0.18)");
    glow(900, 1700, 500, "rgba(0,160,255,0.12)");
    glow(540, 1000, 600, "rgba(255,184,0,0.05)");

    // Header DYD
    ctx.fillStyle = "#CCFF00";
    ctx.font = "bold 38px 'Barlow Condensed', sans-serif";
    ctx.fillText("DYD PADEL", 80, 130);
    ctx.fillStyle = "#fff";
    ctx.font = "20px sans-serif";
    ctx.fillText("DISFRUTA Y DIVIÉRTETE", 80, 160);

    // User name
    ctx.fillStyle = "#fff";
    ctx.font = "bold 92px 'Barlow Condensed', sans-serif";
    const name = (user?.name || "Jugador").toUpperCase();
    ctx.fillText(name, 80, 380);

    ctx.fillStyle = "#CCFF00";
    ctx.font = "26px sans-serif";
    ctx.fillText("MIS ESTADÍSTICAS", 80, 430);

    // 6 KPI tiles (3 cols × 2 rows)
    const tiles = [
      { label: "PTS RETAS",       value: String(kpis.reta_points || 0),     sub: `${kpis.reta_retas_played || 0} jugadas`, color: "#CCFF00" },
      { label: "PTS MARCADORES",  value: String(kpis.match_points || 0),    sub: "Liga 3/2/1/0",                            color: "#34C759" },
      { label: "MARCADORES",      value: String(kpis.matches_count || 0),   sub: "validados",                               color: "#FFB800" },
      { label: "RANK LIGAS",      value: kpis.league_rank ? `#${kpis.league_rank}` : "—", sub: kpis.league_rank_name || "Sin liga", color: "#FF9500" },
      { label: "TIEMPO",          value: fmtTime(kpis.total_play_seconds || 0), sub: "total jugado",                        color: "#00A0FF" },
      { label: "RACHA",           value: `${kpis.current_streak || 0}`,     sub: "días seguidos",                           color: "#FF3B30" },
    ];
    const cols = 2;
    const tileW = 440;
    const tileH = 220;
    const gap = 30;
    const startX = (1080 - (cols * tileW + (cols - 1) * gap)) / 2;
    let row = 0, col = 0;
    tiles.forEach((t, i) => {
      const x = startX + col * (tileW + gap);
      const y = 510 + row * (tileH + gap);
      // tile bg
      ctx.fillStyle = "rgba(255,255,255,0.04)";
      ctx.fillRect(x, y, tileW, tileH);
      // border
      ctx.strokeStyle = t.color + "55";
      ctx.lineWidth = 3;
      ctx.strokeRect(x, y, tileW, tileH);
      // label
      ctx.fillStyle = "#888";
      ctx.font = "bold 22px sans-serif";
      ctx.fillText(t.label, x + 30, y + 50);
      // value
      ctx.fillStyle = t.color;
      ctx.font = "bold 120px 'Barlow Condensed', sans-serif";
      ctx.fillText(t.value, x + 30, y + 160);
      // sub
      ctx.fillStyle = "#999";
      ctx.font = "20px sans-serif";
      ctx.fillText(t.sub, x + 30, y + 195);

      col += 1;
      if (col >= cols) { col = 0; row += 1; }
    });

    // Footer
    ctx.fillStyle = "#666";
    ctx.font = "26px sans-serif";
    ctx.fillText("disfrutaydiviertete.com", 80, 1820);
    ctx.fillStyle = "#CCFF00";
    ctx.font = "bold 28px sans-serif";
    ctx.fillText("⚡ Únete y juega", 80, 1860);

    // QR code (bottom-right corner — profile public link)
    const profileUrl = `${window.location.origin}/player/${user?.id || "anon"}`;
    QRCode.toDataURL(profileUrl, { width: 280, margin: 1, color: { dark: "#0A0A0A", light: "#CCFF00" } })
      .then((qrUrl) => {
        const qrImg = new Image();
        qrImg.onload = () => {
          // Frame
          ctx.fillStyle = "rgba(204,255,0,0.95)";
          ctx.fillRect(820, 1730, 200, 200);
          ctx.drawImage(qrImg, 830, 1740, 180, 180);
          // QR caption
          ctx.fillStyle = "#CCFF00";
          ctx.font = "bold 18px sans-serif";
          ctx.fillText("Escanea mi perfil", 760, 1710);
          setDataUrl(canvas.toDataURL("image/png"));
        };
        qrImg.onerror = () => setDataUrl(canvas.toDataURL("image/png"));
        qrImg.src = qrUrl;
      })
      .catch(() => setDataUrl(canvas.toDataURL("image/png")));
    }, 50);
    return () => clearTimeout(t);
  }, [open, kpis, user]);

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `stats-${(user?.name || "yo").replace(/\s+/g, "_")}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("Imagen descargada");
  };

  const shareNative = async () => {
    if (!dataUrl) return;
    try {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      const file = new File([blob], "stats.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Mis stats DYD Padel" });
        toast.success("Compartido!");
        return;
      }
      // Fallback
      download();
    } catch (e) {
      download();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md max-h-[90vh] overflow-auto" data-testid="share-stats-modal">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] uppercase text-xl flex items-center gap-2">
            <ShareNetwork size={20} className="text-[#CCFF00]" /> Compartir mis stats
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <canvas ref={canvasRef} className="hidden" />
          {dataUrl && (
            <img src={dataUrl} alt="Mis stats" className="w-full max-h-[60vh] object-contain rounded border border-white/10" data-testid="share-stats-preview" />
          )}
          <div className="flex gap-2">
            <Button onClick={download} variant="outline" className="flex-1 border-white/15 text-xs uppercase font-bold" data-testid="share-stats-download">
              <Download size={14} className="mr-1" /> Descargar PNG
            </Button>
            <Button onClick={shareNative} className="flex-1 bg-[#CCFF00] text-black hover:bg-[#B3E600] font-bold text-xs uppercase" data-testid="share-stats-native">
              <ShareNetwork size={14} className="mr-1" /> Compartir
            </Button>
          </div>
          <div className="text-[10px] text-gray-500 italic text-center">Formato Instagram Story 1080×1920</div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
